import * as PIXI from 'pixi.js';
import { gsap } from "gsap";
import { TERRAINS, HEX_SIZE, HEX_HIGHLIGHT_COLOR, ANGLE_RATIO, IDLE_ANIMATION_DURATION } from '@/core/constants/battle.constants';
import { Hex, TerrainType } from '@/core/types/battle.types';
import { axialToPixel } from '@/core/utils/hex.utils';
import { BattleBus } from '@/events/Events';
import { resolveAsset } from '@/core/utils/common.utils';

// ─── Atlas constants ──────────────────────────────────────────────────────────

const PLAINS_PATH     = resolveAsset('@assets/terrain/plains.png');
const PLAINS_COLS     = 4;
const PLAINS_ROW_Y    = 0;
const PLAINS_TEX_SIZE = 256;

const OBSTACLES_PATH       = resolveAsset('@assets/terrain/obstacles.png');
const OBSTACLES_COLS       = 6;
const OBSTACLES_ROW_Y      = 0;
const OBSTACLES_TEX_SIZE_X = 256;
const OBSTACLES_TEX_SIZE_Y = 384;

const CLIFFS_PATH     = resolveAsset('@assets/terrain/hill_top.png');
const CLIFFS_COLS     = 1;
const CLIFFS_ROW_Y    = 128;
const CLIFFS_TEX_SIZE = 256;

const GRASS_PATH         = resolveAsset('@assets/terrain/decals/decals.png');
const GRASS_COLS         = 9;
const GRASS_ROW_Y        = 0;
const GRASS_TEX_SIZE     = 64;
const GRASS_SPAWN_CHANCE = 0.6;
const GRASS_MULTI_CHANCE = 0.3;
const GRASS_MIN_CLUMPS   = 1;
const GRASS_MAX_CLUMPS   = 6;

const BUSHES_PATH         = resolveAsset('@assets/terrain/decals/decals.png');
const BUSHES_COLS         = 7;
const BUSHES_ROW_Y        = 1;
const BUSHES_TEX_SIZE     = 64;
const BUSHES_SPAWN_CHANCE = 0.04;
const BUSHES_MULTI_CHANCE = 0.01;
const BUSHES_MAX_CLUMPS   = 2;

const MOSSES_PATH         = resolveAsset('@assets/terrain/decals/decals.png');
const MOSSES_COLS         = 9;
const MOSSES_ROW_Y        = 2;
const MOSSES_TEX_SIZE     = 64;
const MOSSES_SPAWN_CHANCE = 0.0;
const MOSSES_MULTI_CHANCE = 0.1;
const MOSSES_MAX_CLUMPS   = 2;

const HILL_TOP_PATH = resolveAsset('@assets/terrain/hill_top.png');

const HILL_EDGE_PATHS: Record<number, string> = {
    1: resolveAsset('@assets/terrain/hill_edge_right_rear.png'),
    2: resolveAsset('@assets/terrain/hill_edge_center_rear.png'),
    3: resolveAsset('@assets/terrain/hill_edge_left_rear.png'),
    4: resolveAsset('@assets/terrain/hill_edge_left_front.png'),
    5: resolveAsset('@assets/terrain/hill_edge_center_front.png'),
    0: resolveAsset('@assets/terrain/hill_edge_right_front.png'),
};

// ─── ОПТИМІЗАЦІЯ 1: Глобальна геометрія ──────────────────────────────────
function createHexPolygon(oversize: number = 0): PIXI.Polygon {
    const points: number[] = [];
    const size = HEX_SIZE + oversize;
    
    for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 180) * (60 * i);
        points.push(size * Math.cos(angle), size * Math.sin(angle));
    }
    return new PIXI.Polygon(points);
}

const TEXTURE_CACHE = new Map<string, PIXI.Texture>();

function getCachedTexture(path: string, col: number, row: number, size: number): PIXI.Texture {
    const key = `${path}-${row}-${col}`;
    if (TEXTURE_CACHE.has(key)) {
        return TEXTURE_CACHE.get(key)!;
    }

    const baseTexture = PIXI.Texture.from(path);
    const texture = new PIXI.Texture({
        source: baseTexture.source,
        frame: new PIXI.Rectangle(col * size, row * size, size, size),
    });

    TEXTURE_CACHE.set(key, texture);
    return texture;
}

const SHARED_HIT_AREA = createHexPolygon(1);

export class BattleHex extends PIXI.Container {
    public readonly data: Hex;
    public readonly id: string;
    
    // Візуальні шари
    private highlightOverlay: PIXI.Graphics;
    private staticContainer: PIXI.Container | null = null;
    private edgeContainer: PIXI.Container | null = null;
    private bakedEdgesSprite: PIXI.Sprite | null = null;
    
    private decalsCount: number = 0;

    // Змінна для троттлінгу івентів
    private static lastHoveredId: string | null = null;

    constructor(hexData: Hex) {
        super();

        this.data = hexData;
        this.id   = this.data.id;

        const pos = axialToPixel(this.data.q, this.data.r);
        this.x = Math.round(pos.x);
        this.y = Math.round(pos.y);
        this.scale.y = ANGLE_RATIO;

        this.zIndex = this.data.terrain === TerrainType.Cliff ? this.data.r + 3
                    : this.data.terrain === TerrainType.Hill  ? this.data.r + 2
                    : this.data.r + 1;

        // ─── ІНТЕРАКТИВНА ЧАСТИНА ─────────────────────────────────────────────
        // BattleHex тепер відповідає ТІЛЬКИ за підсвітку та івенти.
        
        this.highlightOverlay = new PIXI.Graphics();
        this.highlightOverlay.eventMode = 'none';
        this.addChild(this.highlightOverlay);

        // this.hitArea   = SHARED_HIT_AREA; 
        this.eventMode = 'none';
        // this.cursor = 'pointer'; // Розкоментуй, коли вимкнеш CursorManager

        // this.setupInteraction();
    }

    // ─── СТАТИЧНА ЧАСТИНА (Викликається ззовні для mapLayer) ───────────────

    /**
     * Повертає контейнер з усією статичною графікою (земля, кущі, краї).
     * Його потрібно додати в `mapLayer` (або запекти).
     */
    public getStaticVisuals(): PIXI.Container {
        if (this.staticContainer) return this.staticContainer;

        this.staticContainer = new PIXI.Container();
        this.staticContainer.eventMode = 'none';
        this.staticContainer.interactiveChildren = false;

        // Наслідуємо координати гекса, щоб контейнер став на своє місце в mapLayer
        this.staticContainer.x = this.x;
        this.staticContainer.y = this.y;
        this.staticContainer.scale.y = this.scale.y;
        this.staticContainer.zIndex = this.zIndex;

        // 1. Plains / Hill Top підкладка
        if (this.data.terrain === TerrainType.Hill) {
            const top = this.createAtlasSprite(HILL_TOP_PATH, 1, 0, 256, 256, false);
            if (top) this.staticContainer.addChild(top);

            this.edgeContainer = new PIXI.Container();
            this.edgeContainer.eventMode = 'none';
            this.staticContainer.addChild(this.edgeContainer);
        } else {
            const plains = this.createAtlasSprite(
                PLAINS_PATH, PLAINS_COLS, PLAINS_ROW_Y, PLAINS_TEX_SIZE, PLAINS_TEX_SIZE, true
            );
            if (plains) this.staticContainer.addChild(plains);
        }

        // 2. Cliff overlay
        if (this.data.terrain === TerrainType.Cliff) {
            const cliff = this.createAtlasSprite(
                CLIFFS_PATH, CLIFFS_COLS, CLIFFS_ROW_Y, CLIFFS_TEX_SIZE, CLIFFS_TEX_SIZE
            );
            if (cliff) this.staticContainer.addChild(cliff);
        }

        // 3. Деколі (кущі, трава)
        if (this.data.terrain === TerrainType.Plains && !this.data.isObstacle) {
            this.addDecals(this.staticContainer);
        }

        return this.staticContainer;
    }

    // ─── Sprite creation ──────────────────────────────────────────────────────

    private createAtlasSprite(
        path: string, cols: number, rowY: number, texW: number, texH: number,
        flipHorizontal: boolean = false, flipVertical: boolean = false,
    ): PIXI.Sprite | null {
        try {
            const baseTexture  = PIXI.Texture.from(path);
            const col          = Math.floor(Math.random() * cols);
            const frameTexture = new PIXI.Texture({
                source: baseTexture.source,
                frame:  new PIXI.Rectangle(col * texW, rowY, texW, texH),
            });

            const sprite      = new PIXI.Sprite(frameTexture);
            const aspectRatio = texH / texW;
            
            sprite.anchor.set(0.5, aspectRatio === 1 ? 0.5 : texW/texH);
            sprite.width  = HEX_SIZE * 2;
            sprite.height = HEX_SIZE * 2 * aspectRatio;
            
            if (flipHorizontal && Math.random() < 0.5) sprite.scale.x *= -1;
            if (flipVertical && Math.random() < 0.5) sprite.scale.y *= -1;
            
            sprite.eventMode = 'none';
            return sprite;
        } catch (e) {
            console.warn('[BattleHex] Failed to create sprite:', path, e);
            return null;
        }
    }

    // ─── Decals ───────────────────────────────────────────────────────────────

    private addDecals(container: PIXI.Container): void {
        container.sortableChildren = true;

        if (Math.random() <= GRASS_SPAWN_CHANCE) {
            const numGrass = Math.max(GRASS_MIN_CLUMPS, Math.random() < GRASS_MULTI_CHANCE 
                ? Math.floor(Math.random() * GRASS_MAX_CLUMPS) + 1 : 1);
            for (let i = 0; i < numGrass; i++) {
                this.createScatterSprite(container, GRASS_PATH, GRASS_COLS, GRASS_ROW_Y, GRASS_TEX_SIZE, 1.0);
            }
        }

        if (Math.random() <= BUSHES_SPAWN_CHANCE) {
            const numBushes = Math.random() < BUSHES_MULTI_CHANCE 
                ? Math.floor(Math.random() * BUSHES_MAX_CLUMPS) + 1 : 1;
            for (let i = 0; i < numBushes; i++) {
                this.createScatterSprite(container, BUSHES_PATH, BUSHES_COLS, BUSHES_ROW_Y, BUSHES_TEX_SIZE, 1.0, 0, 0.8);
            }
        }

        if (Math.random() <= MOSSES_SPAWN_CHANCE) {
            const numMosses = Math.random() < MOSSES_MULTI_CHANCE 
                ? Math.floor(Math.random() * MOSSES_MAX_CLUMPS) + 1 : 1;
            for (let i = 0; i < numMosses; i++) {
                this.createScatterSprite(container, MOSSES_PATH, MOSSES_COLS, MOSSES_ROW_Y, MOSSES_TEX_SIZE, 1.0);
            }
        }

        container.sortableChildren = false;
    }

    private createScatterSprite(
        container: PIXI.Container, path: string, cols: number, row: number, 
        size: number, baseScale: number, minRadius: number = 10, opacity: number = 1
    ): void {
        try {
            const col = Math.floor(Math.random() * cols);
            const frameTexture = getCachedTexture(path, col, row, size);

            const sprite = new PIXI.Sprite(frameTexture);
            sprite.anchor.set(0.5, 1);

            const rBase = Math.sqrt(Math.random());
            const maxR = HEX_SIZE * 0.6;
            const radius = minRadius + rBase * (maxR - minRadius);
            const angle = Math.random() * Math.PI * 2;
            
            sprite.x = radius * Math.cos(angle);
            sprite.y = (radius * Math.sin(angle)) + 42;
            sprite.scale.y = 1 / ANGLE_RATIO;
            
            if (opacity !== 1) sprite.alpha = opacity;

            const randomScale = baseScale * (0.8 + Math.random() * 0.4);
            sprite.scale.x *= randomScale;
            sprite.scale.y *= randomScale;

            sprite.eventMode = 'none';
            sprite.zIndex = sprite.y; 

            this.decalsCount += 1;
            container.addChild(sprite);
        } catch (e) {
            console.warn(`[BattleHex] Failed to create scatter decor (${path}):`, e);
        }
    }

    // ─── Obstacles ────────────────────────────────────────────────────────────

    public getObstacleSprite(): PIXI.Sprite | null {
        if (!this.data.isObstacle) return null;

        const obstacle = this.createAtlasSprite(
            OBSTACLES_PATH, OBSTACLES_COLS, OBSTACLES_ROW_Y, OBSTACLES_TEX_SIZE_X, OBSTACLES_TEX_SIZE_Y, true, false
        );
        if (!obstacle) return null;
        
        obstacle.x = this.x;
        obstacle.y = this.data.terrain === TerrainType.Hill ? this.y - 30 : this.y - 10;
        obstacle.zIndex = obstacle.y;

        return obstacle;
    }

    // ─── Hill edges ───────────────────────────────────────────────────────────

    public addEdgeSprite(direction: number): void {
        if (!this.edgeContainer) return;
        const path = HILL_EDGE_PATHS[direction];
        if (!path) return;

        try {
            const sprite = new PIXI.Sprite(PIXI.Texture.from(path));
            sprite.anchor.set(0.5, 0.5);
            sprite.width     = HEX_SIZE * 2;
            sprite.height    = HEX_SIZE * 2;
            sprite.eventMode = 'none';

            this.edgeContainer.addChild(sprite);
        } catch (e) {
            console.warn('[BattleHex] Failed to add edge sprite:', path, e);
        }
    }

    public bakeEdges(renderer: PIXI.Renderer): PIXI.RenderTexture | null {
        if (!this.edgeContainer) return null;

        const size = HEX_SIZE * 2;
        const renderTexture = PIXI.RenderTexture.create({ width: size, height: size });

        this.edgeContainer.x = size / 2;
        this.edgeContainer.y = size / 2;

        renderer.render({ container: this.edgeContainer, target: renderTexture });

        this.edgeContainer.x = 0;
        this.edgeContainer.y = 0;

        this.applyBakedEdges(renderTexture);
        return renderTexture;
    }

    public applyBakedEdges(renderTexture: PIXI.RenderTexture): void {
        if (!this.edgeContainer || !this.staticContainer) return;

        const size = HEX_SIZE * 2;
        const index = this.staticContainer.getChildIndex(this.edgeContainer);
        
        this.staticContainer.removeChild(this.edgeContainer);
        this.edgeContainer.destroy({ children: true });
        this.edgeContainer = null;

        this.bakedEdgesSprite = new PIXI.Sprite(renderTexture);
        this.bakedEdgesSprite.anchor.set(0.5, 0.5);
        this.bakedEdgesSprite.width     = size;
        this.bakedEdgesSprite.height    = size;
        this.bakedEdgesSprite.eventMode = 'none';

        this.staticContainer.addChildAt(this.bakedEdgesSprite, index);
    }

    // ─── Interaction (ОПТИМІЗОВАНО) ───────────────────────────────────────────

    private setupInteraction(): void {
        this.on('pointerover', (e: PIXI.FederatedPointerEvent) => {
            // // Фільтрація: ігноруємо події, якщо ми все ще над тим самим гексом
            // if (BattleHex.lastHoveredId === this.id) return;
            // BattleHex.lastHoveredId = this.id;

            BattleBus.emit('HEX_HOVER', { id: this.id, position: { x: e.global.x, y: e.global.y } });
            if (this.data.entityId) {
                BattleBus.emit('UNIT_HOVER', { id: this.data.entityId, position: { x: e.global.x, y: e.global.y } });
            }
        });

        this.on('pointerout', () => {
            // Очищуємо тільки якщо ми дійсно покинули поточний гекс
            // if (BattleHex.lastHoveredId === this.id) {
            //     BattleHex.lastHoveredId = null;
                BattleBus.emit('HEX_OUT', { id: this.id });
                if (this.data.entityId) {
                    BattleBus.emit('UNIT_OUT', { id: this.data.entityId });
                }
            // }
        });

        this.on('pointertap', () => {
            BattleBus.emit('HEX_CLICK', { id: this.id });
            console.log("hexid", this.id, "decals:", this.decalsCount, this.data);
        });
    }

    // ─── Highlight ────────────────────────────────────────────────────────────

    public applyHighlight(style: any | null): void {
        gsap.killTweensOf(this.highlightOverlay);
        
        if (!style) {
            this.highlightOverlay.visible = false;
            this.highlightOverlay.alpha = 0;
            return;
        }

        this.highlightOverlay.visible = true;
        this.highlightOverlay.alpha = 1;
        this.highlightOverlay.clear();

        this.highlightOverlay
            .poly(SHARED_HIT_AREA.points)
            .fill({ color: style.tint, alpha: style.fillAlpha });

        if (style.borderThickness > 0) {
            this.highlightOverlay.stroke({
                width: style.borderThickness,
                color: style.tint,
                alpha: style.borderAlpha,
                alignment: 1 
            });
        }
        
        if (style.isPulse) {
            gsap.to(this.highlightOverlay, {
                alpha: 0.5,
                duration: IDLE_ANIMATION_DURATION,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut"
            });
        }

        this.highlightOverlay.blendMode = style.blendMode || 'normal';
        const safeScale = style.scale >= 1 ? 0.985 : style.scale;
        this.highlightOverlay.scale.set(safeScale);
    }

    public containsPoint(x: number, y: number): boolean {
        const localPoint = this.toLocal(new PIXI.Point(x, y));
        return SHARED_HIT_AREA.contains(localPoint.x, localPoint.y);
    }

    // ─── Utils ────────────────────────────────────────────────────────────────

    public getHighlightAlpha(): number { return this.highlightOverlay.alpha; }
    public occupy(unitId: string): void { this.data.entityId = unitId; }
    public vacate(): void { this.data.entityId = undefined; }
    public get occupantId(): string | undefined { return this.data.entityId; }

    public clearStaticReferences(): void {
        // Просто зануляємо посилання. Об'єкти вже знищені чанком.
        this.staticContainer = null;
        this.edgeContainer = null;
    }

    public destroy(): void {
        this.removeAllListeners();
        if (this.staticContainer) this.staticContainer.destroy({ children: true });
        super.destroy({ children: true });
    }
}